import { formatLocalDate, parseLocalDate } from '../shared/dateValues';
import type { Airport } from './api/flights.types';

/** Ages the flights backend uses to classify a passenger. */
export const PASSENGER_AGES = { adult: 30, child: 7, infant: 1 } as const;

export type PassengerKind = keyof typeof PASSENGER_AGES;

export interface PassengerCounts {
  adults: number;
  children: number;
  infants: number;
}

export interface FlightSearchValues {
  origin: Airport | null;
  destination: Airport | null;
  departureDate: Date | null;
  returnDate: Date | null;
  passengers: PassengerCounts;
  isOnlyOneWay: boolean;
  currency: string;
}

export const EMPTY_FLIGHT_SEARCH: FlightSearchValues = {
  currency: 'USD',
  departureDate: null,
  destination: null,
  isOnlyOneWay: false,
  origin: null,
  passengers: { adults: 1, children: 0, infants: 0 },
  returnDate: null,
};

/** `passengers` travels the URL as a comma-joined list of ages: `30,30,7`. */
export function passengerAges(counts: PassengerCounts): number[] {
  return [
    ...Array<number>(Math.max(counts.adults, 0)).fill(PASSENGER_AGES.adult),
    ...Array<number>(Math.max(counts.children, 0)).fill(PASSENGER_AGES.child),
    ...Array<number>(Math.max(counts.infants, 0)).fill(PASSENGER_AGES.infant),
  ];
}

export function passengerCountsFromAges(ages: number[] | null | undefined): PassengerCounts {
  const list = Array.isArray(ages) ? ages : [];
  const counts: PassengerCounts = {
    adults: list.filter((age) => age === PASSENGER_AGES.adult).length,
    children: list.filter((age) => age === PASSENGER_AGES.child).length,
    infants: list.filter((age) => age === PASSENGER_AGES.infant).length,
  };

  // The backend rejects a search with zero travellers, and the legacy form
  // silently repaired that too.
  if (counts.adults + counts.children + counts.infants === 0) {
    return { ...counts, adults: 1 };
  }

  return counts;
}

export function totalPassengers(counts: PassengerCounts): number {
  return counts.adults + counts.children + counts.infants;
}

function parseAirport(
  cityCode: string | null,
  cityName: string | null,
  numericId: string | null,
): Airport | null {
  const code = (cityCode || '').trim();
  const name = (cityName || '').trim();

  if (!code || !name) return null;

  const id = Number(numericId);

  return {
    cityCode: code,
    cityName: name,
    id: numericId && Number.isFinite(id) ? id : null,
  };
}

/** Reads a `/buscarxvuelos` URL back into form values. */
export function parseFlightSearchValues(search: string): FlightSearchValues {
  if (!search) return EMPTY_FLIGHT_SEARCH;

  const params = new URLSearchParams(search);
  const isOnlyOneWay = params.get('isOnlyOneWay') === 'true';
  const ages = (params.get('passengers') || '')
    .split(',')
    .map(Number)
    .filter((age) => Number.isFinite(age));

  return {
    currency: params.get('currency') || 'USD',
    departureDate: parseLocalDate(params.get('departureDate')),
    destination: parseAirport(
      params.get('destinationId'),
      params.get('destinationName'),
      params.get('destinationIdn'),
    ),
    isOnlyOneWay,
    origin: parseAirport(params.get('originId'), params.get('originName'), params.get('originIdn')),
    passengers: passengerCountsFromAges(ages),
    returnDate: isOnlyOneWay ? null : parseLocalDate(params.get('returnDate')),
  };
}

interface BuildFlightSearchQueryOptions {
  cache: boolean;
  includeRefresh?: boolean;
  refreshToken?: number | string;
}

/**
 * Builds the `/buscarxvuelos` query string.
 *
 * Key order and value shape mirror the legacy `serializeFlightSearchParams`
 * exactly — the results page and its cache keys read these params, so any drift
 * here would silently break flight searches. `flightSearchValues.test.ts` pins
 * that parity against the legacy implementation.
 */
export function buildFlightSearchQuery(
  values: FlightSearchValues,
  options: BuildFlightSearchQueryOptions,
): string {
  const params = new URLSearchParams();

  params.set('departureDate', formatLocalDate(values.departureDate));

  if (values.returnDate && !values.isOnlyOneWay) {
    params.set('returnDate', formatLocalDate(values.returnDate));
  }

  if (values.origin) {
    params.set('originId', values.origin.cityCode);
    params.set('originName', values.origin.cityName);
    if (values.origin.id) {
      params.set('originIdn', String(values.origin.id));
    }
  }

  if (values.destination) {
    params.set('destinationId', values.destination.cityCode);
    params.set('destinationName', values.destination.cityName);
    if (values.destination.id) {
      params.set('destinationIdn', String(values.destination.id));
    }
  }

  params.set('passengers', passengerAges(values.passengers).join(','));
  params.set('isOnlyOneWay', values.isOnlyOneWay ? 'true' : 'false');

  if (values.currency) params.set('currency', values.currency);

  params.set('cache', String(options.cache));

  if (options.includeRefresh) {
    params.set('_refresh', String(options.refreshToken ?? Date.now()));
  }

  return `?${params.toString()}`;
}
